import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { createTheme, Theme } from '@mui/material/styles';
import { designTokens } from '../theme/designTokens';
import apiClient from '../services/api/config';

interface CompanyBranding {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  sidebarColor: string;
  sidebarTextColor: string;
  headerColor: string;
  headerTextColor: string;
  logoUrl: string;
  companyName: string;
  airaIconUrl: string;
}

interface BrandingContextType {
  branding: CompanyBranding;
  theme: Theme;
  refreshBranding: () => Promise<void>;
}

const defaultBranding: CompanyBranding = {
  primaryColor: '#C0EB38',
  secondaryColor: '#3B82F6',
  accentColor: '#F59E0B',
  sidebarColor: '#1A1D1F',
  sidebarTextColor: '#FFFFFF',
  headerColor: '#FFFFFF',
  headerTextColor: '#1A1D1F',
  logoUrl: '',
  companyName: '',
  airaIconUrl: '',
};

function buildTheme(b: CompanyBranding): Theme {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: b.primaryColor || defaultBranding.primaryColor,
        light: designTokens.colors.primary[300],
        dark: designTokens.colors.primary[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: b.secondaryColor || defaultBranding.secondaryColor,
        light: designTokens.colors.secondary[300],
        dark: designTokens.colors.secondary[700],
        contrastText: '#FFFFFF',
      },
      error: {
        main: designTokens.colors.error.main,
        light: designTokens.colors.error.light,
        dark: designTokens.colors.error.dark,
      },
      warning: {
        main: designTokens.colors.warning.main,
        light: designTokens.colors.warning.light,
        dark: designTokens.colors.warning.dark,
      },
      info: {
        main: designTokens.colors.info.main,
        light: designTokens.colors.info.light,
        dark: designTokens.colors.info.dark,
      },
      success: {
        main: designTokens.colors.success.main,
        light: designTokens.colors.success.light,
        dark: designTokens.colors.success.dark,
      },
      background: {
        default: designTokens.colors.background.default,
        paper: designTokens.colors.background.paper,
      },
      text: {
        primary: designTokens.colors.text.primary,
        secondary: designTokens.colors.text.secondary,
        disabled: designTokens.colors.text.disabled,
      },
      divider: designTokens.colors.border.main,
    },
    typography: {
      fontFamily: designTokens.typography.fontFamily.primary,
      h1: { fontSize: designTokens.typography.fontSize['5xl'], fontWeight: designTokens.typography.fontWeight.bold, lineHeight: designTokens.typography.lineHeight.tight, letterSpacing: designTokens.typography.letterSpacing.tight },
      h2: { fontSize: designTokens.typography.fontSize['4xl'], fontWeight: designTokens.typography.fontWeight.bold, lineHeight: designTokens.typography.lineHeight.tight, letterSpacing: designTokens.typography.letterSpacing.tight },
      h3: { fontSize: designTokens.typography.fontSize['3xl'], fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.tight },
      h4: { fontSize: designTokens.typography.fontSize['2xl'], fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.normal },
      h5: { fontSize: designTokens.typography.fontSize.xl, fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.normal },
      h6: { fontSize: designTokens.typography.fontSize.lg, fontWeight: designTokens.typography.fontWeight.semibold, lineHeight: designTokens.typography.lineHeight.normal },
      body1: { fontSize: designTokens.typography.fontSize.base, lineHeight: designTokens.typography.lineHeight.relaxed, fontWeight: designTokens.typography.fontWeight.regular },
      body2: { fontSize: designTokens.typography.fontSize.sm, lineHeight: designTokens.typography.lineHeight.normal, fontWeight: designTokens.typography.fontWeight.regular },
    },
    spacing: 8,
    shape: { borderRadius: parseInt(designTokens.borderRadius.base) },
    shadows: [
      'none',
      designTokens.shadows.sm, designTokens.shadows.base, designTokens.shadows.md,
      designTokens.shadows.lg, designTokens.shadows.xl, designTokens.shadows['2xl'],
      designTokens.shadows.xl, designTokens.shadows['2xl'], designTokens.shadows['2xl'],
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
      designTokens.shadows['2xl'], designTokens.shadows['2xl'], designTokens.shadows['2xl'],
    ],
    components: {
      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(4px)' },
          invisible: { backgroundColor: 'transparent', backdropFilter: 'none' },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: designTokens.typography.fontWeight.semibold,
            borderRadius: parseInt(designTokens.borderRadius.base),
            padding: `${designTokens.spacing[2]} ${designTokens.spacing[6]}`,
            transition: designTokens.transitions.base,
          },
          sizeLarge: {
            padding: `${designTokens.spacing[3]} ${designTokens.spacing[8]}`,
            fontSize: designTokens.typography.fontSize.lg,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: designTokens.shadows.base,
            borderRadius: parseInt(designTokens.borderRadius.lg),
            border: `1px solid ${designTokens.colors.border.light}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: parseInt(designTokens.borderRadius.base),
            },
          },
        },
      },
    },
  });
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<CompanyBranding>(defaultBranding);

  const applyCSS = (b: CompanyBranding) => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', b.primaryColor);
    root.style.setProperty('--brand-secondary', b.secondaryColor);
    root.style.setProperty('--brand-accent', b.accentColor);
    root.style.setProperty('--brand-sidebar', b.sidebarColor);
    root.style.setProperty('--brand-sidebar-text', b.sidebarTextColor);
    root.style.setProperty('--brand-header', b.headerColor);
    root.style.setProperty('--brand-header-text', b.headerTextColor);
  };

  const loadBranding = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await apiClient.get('/company/branding');
      if (response.data?.success && response.data?.data) {
        const d = response.data.data;
        const newBranding = {
          primaryColor: d.primaryColor || defaultBranding.primaryColor,
          secondaryColor: d.secondaryColor || defaultBranding.secondaryColor,
          accentColor: d.accentColor || defaultBranding.accentColor,
          sidebarColor: d.sidebarColor || defaultBranding.sidebarColor,
          sidebarTextColor: d.sidebarTextColor || defaultBranding.sidebarTextColor,
          headerColor: d.headerColor || defaultBranding.headerColor,
          headerTextColor: d.headerTextColor || defaultBranding.headerTextColor,
          logoUrl: d.logoUrl || '',
          companyName: d.companyName || '',
          airaIconUrl: d.airaIconUrl || '',
        };
        setBranding(newBranding);
        applyCSS(newBranding);
      }
    } catch (err) {
      // Use defaults
    }
  };

  useEffect(() => {
    applyCSS(defaultBranding);
    loadBranding();
    const handleStorage = () => loadBranding();
    const handleLogin = () => loadBranding();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('auth-login', handleLogin);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('auth-login', handleLogin);
    };
  }, []);

  const theme = useMemo(() => buildTheme(branding), [branding]);

  const value = useMemo(() => ({
    branding,
    theme,
    refreshBranding: loadBranding,
  }), [branding, theme]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    return {
      branding: defaultBranding,
      theme: buildTheme(defaultBranding),
      refreshBranding: async () => {},
    };
  }
  return context;
}
